import { useEffect } from 'react';

import { useFirstRender } from 'hooks/useFirstRender';
import { GatewayRulesFormRemoveButton } from 'pages/GatewayRules/components/GatewayRulesForm/GatewayRulesFormRemoveButton';
import {
  GatewayRulesFormValues,
  MappingItemFormValues,
} from 'pages/GatewayRules/components/GatewayRulesForm/hooks/useGatewayRulesForm';
import { getInitialMappingItem } from 'pages/GatewayRules/components/GatewayRulesForm/utils';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { Stack } from 'shared/mui/Stack/Stack';
import {
  AutocompleteArrayInput,
  AutocompleteInput,
  EnumAutocompleteInput,
  NumberInput,
} from 'shared/react-admin/Inputs';
import { Commission } from 'types/embedded/commission';
import { Gateway } from 'types/embedded/gateway';
import { getId, jsonEqual } from 'utils';

import { GatewayRulesFormMappingCommissionsOption } from './GatewayRulesFormMappingCommissionsOption';
import { GatewayRulesFormMappingItemWrapper } from './GatewayRulesFormMappingItem.styled';
import { useAutoSelectCommissions } from './hooks/useAutoSelectCommissions';
import { useAutoSelectGateway } from './hooks/useAutoSelectGateway';
import { useAutoSelectMerchantAccount } from './hooks/useAutoSelectMerchantAccount';
import { useGatewayChoices } from './hooks/useGatewayChoices';
import { useGetAvailableCommissions } from './hooks/useGetAvailableCommissions';
import { getCommissionsFilter } from './utils/getCommissionsFilter';
import { useGatewayRulesDictionaryContext } from '../context/GatewayRulesDictionaryContext';
import { GatewayRulesFormSkeletonMappingItem } from '../GatewayRulesFormSkeleton/GatewayRulesFormSkeletonMappingItem';
import { useMerchantAccounts } from './hooks/useMerchantAccounts';

type Props = {
  direction?: string | null;
  mappingIndex: number;
  mappingItemIndex: number;
  enableAdditionalFields: boolean;
  onRemoveMappingItemClick: (index: number) => void;
  gateways: Gateway[];
  isLoadingGateways: boolean;
};

export const GatewayRulesFormMappingItem: React.FC<Props> = ({
  direction,
  gateways,
  isLoadingGateways,
  mappingItemIndex,
  mappingIndex,
  enableAdditionalFields,
  onRemoveMappingItemClick,
}) => {
  const translate = useTypedTranslate();
  const { control, setValue } = useFormContext<GatewayRulesFormValues>();
  const { isFirstRender } = useFirstRender();

  const merchantWatched = useWatch({ control, name: 'merchant' });
  const methodWatched = useWatch({ control, name: `mappings.${mappingIndex}.method` });
  const directionWatched = useWatch({ control, name: `mappings.${mappingIndex}.direction` });
  const gatewayWatched = useWatch({
    control,
    name: `mappings.${mappingIndex}.mappingItems.${mappingItemIndex}.gateway`,
  });
  const mappingItems = useWatch({ control, name: `mappings.${mappingIndex}.mappingItems` });

  const isDisabledRemoveMappingItem =
    mappingItems?.length === 1 && jsonEqual(mappingItems[0], getInitialMappingItem());

  const { transactionTypes } = useGatewayRulesDictionaryContext();
  const { accounts, accountWatched, accountChoices, currency, isLoadingAccounts } =
    useMerchantAccounts({
      mappingIndex,
      mappingItemIndex,
    });

  const { commissions, isLoadingCommissions } = useGetAvailableCommissions({
    filter: {
      currency: currency?.id !== undefined ? String(currency.id) : '',
      merchant:
        typeof merchantWatched === 'object'
          ? getId(merchantWatched?.['@id'])
          : getId(merchantWatched),
      gateways: getId(gatewayWatched),
      transactionTypes: getCommissionsFilter(transactionTypes, direction),
    },
    enabled: Boolean(transactionTypes?.length) && Boolean(direction) && Boolean(currency),
  });

  const { gatewayChoices } = useGatewayChoices({
    mappingIndex,
    mappingItemIndex,
    gateways,
    isLoadingGateways,
  });

  const isMappingItemLoading = isLoadingAccounts || isLoadingCommissions;

  useAutoSelectGateway({ mappingIndex, mappingItemIndex, gateways });
  useAutoSelectMerchantAccount({ mappingIndex, mappingItemIndex, accounts });
  useAutoSelectCommissions({ mappingIndex, mappingItemIndex, commissions });

  useEffect(() => {
    if (!isFirstRender) {
      setValue(
        `mappings.${mappingIndex}.mappingItems.${mappingItemIndex}`,
        getInitialMappingItem() as MappingItemFormValues,
      );
      setValue(`mappings.${mappingIndex}.mappingItems.${mappingItemIndex}.commissions`, []);
    }
  }, [merchantWatched]);

  const itemPath = `mappings.${mappingIndex}.mappingItems.${mappingItemIndex}` as const;

  if (isMappingItemLoading) {
    return (
      <GatewayRulesFormMappingItemWrapper>
        <GatewayRulesFormSkeletonMappingItem />
      </GatewayRulesFormMappingItemWrapper>
    );
  }

  return (
    <GatewayRulesFormMappingItemWrapper>
      <Box width="100%">
        <Box display="flex" gap={2} width="100%">
          <AutocompleteInput
            choices={gatewayChoices}
            fullWidth
            isRequired
            label={translate('app.table.gateway')}
            name={`${itemPath}.gateway`}
            onChange={() => {
              setValue(`${itemPath}.commissions`, []);
              setValue(`${itemPath}.merchantAccount`, '');
            }}
            optionText="name"
            readOnly={!merchantWatched || !methodWatched || !directionWatched}
            source={`${itemPath}.gateway`}
            variant="outlined"
          />
          <AutocompleteInput
            choices={accountChoices}
            fullWidth
            isRequired
            label={translate('app.table.account')}
            name={`${itemPath}.merchantAccount`}
            onChange={() => {
              setValue(`${itemPath}.commissions`, []);
            }}
            optionText="name"
            readOnly={!gatewayWatched}
            source={`${itemPath}.merchantAccount`}
            variant="outlined"
          />
          <NumberInput
            InputProps={{
              inputProps: { min: 0, max: 100 },
            }}
            isRequired
            label={translate('app.table.trafficPercentage')}
            name={`${itemPath}.value`}
            source={`${itemPath}.value`}
            sx={{ maxWidth: '240px' }}
            variant="outlined"
          />
        </Box>
        <Stack direction="row" gap={2}>
          <AutocompleteArrayInput
            choices={commissions ? [...commissions] : []}
            clearOnBlur={true}
            fullWidth
            inputText={(choice: Commission): string => choice?.name}
            isRequired
            label={translate('app.table.commissions')}
            matchSuggestion={(filter: string, choice: Commission): boolean =>
              choice.name.toLowerCase().includes(filter.toLowerCase())
            }
            name={`${itemPath}.commissions`}
            optionText={<GatewayRulesFormMappingCommissionsOption />}
            readOnly={!gatewayWatched || !accountWatched}
            size="small"
            source={`${itemPath}.commissions`}
            variant="outlined"
          />
          {enableAdditionalFields && (
            <EnumAutocompleteInput
              filterToQuery={(searchText: string) => ({ description: searchText })}
              fullWidth
              helperText={false}
              label={translate('app.table.badge')}
              name={`${itemPath}.badge`}
              optionText="description"
              resource="badges"
              source={`${itemPath}.badge`}
              variant="outlined"
            />
          )}
          <NumberInput
            InputProps={{
              inputProps: { min: 0, max: 100 },
            }}
            helperText={false}
            isRequired
            label={translate('app.merchantAccounts.subagentId')}
            name={`${itemPath}.subagent`}
            source={`${itemPath}.subagent`}
            sx={{ maxWidth: '240px' }}
            variant="outlined"
          />
        </Stack>
      </Box>
      <GatewayRulesFormRemoveButton
        disabled={isDisabledRemoveMappingItem}
        onClick={() => onRemoveMappingItemClick(mappingItemIndex)}
        sx={{ marginBottom: '20px' }}
        tooltipText={translate('app.gatewayRules.deleteGateway')}
      />
    </GatewayRulesFormMappingItemWrapper>
  );
};
